import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Loader2, ArrowRight, ArrowUpRight, CheckCircle2, Rocket, Target, Mail, FileText } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ConsultationDialog from "@/components/ConsultationDialog";
import { Reveal } from "@/lib/motion";
import { growthAdvisorRecommend, growthAdvisorProposal } from "@/lib/api";

const GOALS = ["More website visitors", "More phone calls", "More inquiries", "More appointments", "More online sales", "More local customers", "More B2B customers"];
const LEADS = ["0–10 / month", "10–50 / month", "50–200 / month", "200+ / month"];
const WEBSITE = [{ v: "yes", l: "Yes, it's modern" }, { v: "outdated", l: "Yes, but it's outdated" }, { v: "no", l: "No website yet" }];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-sm font-semibold text-slate-700">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);

export default function GrowthAdvisor() {
  const [form, setForm] = useState({ business_type: "", location: "", offerings: "", has_website: "", current_leads: "", primary_goal: "" });
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [consultOpen, setConsultOpen] = useState(false);
  const [pName, setPName] = useState("");
  const [pEmail, setPEmail] = useState("");
  const [pPhone, setPPhone] = useState("");
  const [pSubmitting, setPSubmitting] = useState(false);
  const [pSent, setPSent] = useState(null);

  useEffect(() => { window.scrollTo(0, 0); document.title = "AI Growth Advisor | WebsiteTech.pro"; }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_type.trim()) { toast.error("Tell us what kind of business you run."); return; }
    setLoading(true); setPlan(null);
    try {
      const r = await growthAdvisorRecommend(form);
      setPlan(r);
      setTimeout(() => document.getElementById("growth-plan")?.scrollIntoView({ behavior: "smooth" }), 200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "The advisor is busy — please try again.");
    } finally { setLoading(false); }
  };

  const inputCls = "w-full bg-white border border-slate-200 rounded-lg px-3.5 py-2.5 text-sm text-slate-900 outline-none focus:border-brand";

  const sendProposal = async () => {
    if (!pEmail.trim()) { toast.error("Please enter your email to receive the proposal."); return; }
    setPSubmitting(true);
    try {
      const res = await growthAdvisorProposal({
        name: pName, email: pEmail, phone: pPhone,
        business_type: form.business_type, location: form.location, origin: window.location.origin,
        headline: plan.headline, summary: plan.summary, recommended: plan.recommended,
        suggested_bundle: plan.suggested_bundle, first_steps: plan.first_steps,
        expected_outcomes: plan.expected_outcomes,
      });
      setPSent(res);
      toast.success("Your branded proposal is on its way to your inbox!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setPSubmitting(false); }
  };

  return (
    <>
      <Navbar />
      <main className="pt-24" data-testid="growth-advisor">
        <section className="hero-bg border-b border-slate-100">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
            <span className="inline-flex items-center gap-2 overline"><Sparkles size={14} className="text-brand" /> AI Growth Advisor</span>
            <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight mt-3 leading-[1.05]">Get Your Personalized Growth Plan</h1>
            <p className="text-lg text-slate-500 mt-5">Answer a few quick questions and our AI recommends the exact combination of WebsiteTech services to get you more leads and customers.</p>
          </div>
        </section>

        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 card-shadow grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="advisor-form">
            <Field label="What type of business do you run?">
              <input value={form.business_type} onChange={set("business_type")} placeholder="e.g. dental clinic, hotel, law firm" data-testid="advisor-business" className={inputCls} />
            </Field>
            <Field label="Where are your customers?">
              <input value={form.location} onChange={set("location")} placeholder="e.g. Kochi, Kerala" data-testid="advisor-location" className={inputCls} />
            </Field>
            <div className="sm:col-span-2">
              <Field label="What do you sell / offer?">
                <input value={form.offerings} onChange={set("offerings")} placeholder="e.g. implants, braces, teeth whitening" data-testid="advisor-offerings" className={inputCls} />
              </Field>
            </div>
            <Field label="Do you have a website?">
              <select value={form.has_website} onChange={set("has_website")} data-testid="advisor-website" className={inputCls}>
                <option value="">Select…</option>
                {WEBSITE.map((w) => <option key={w.v} value={w.v}>{w.l}</option>)}
              </select>
            </Field>
            <Field label="How many leads do you get now?">
              <select value={form.current_leads} onChange={set("current_leads")} data-testid="advisor-leads" className={inputCls}>
                <option value="">Select…</option>
                {LEADS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </Field>
            <div className="sm:col-span-2">
              <Field label="What's your primary goal?">
                <select value={form.primary_goal} onChange={set("primary_goal")} data-testid="advisor-goal" className={inputCls}>
                  <option value="">Select…</option>
                  {GOALS.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </Field>
            </div>
            <div className="sm:col-span-2">
              <button type="submit" disabled={loading} data-testid="advisor-submit" className="btn-primary rounded-xl px-6 py-3.5 flex items-center justify-center gap-2 text-sm w-full sm:w-auto disabled:opacity-60">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Rocket size={16} />} {loading ? "Building your plan…" : "Get My Growth Plan"}
              </button>
            </div>
          </form>
        </section>

        {plan && (
          <section id="growth-plan" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20" data-testid="advisor-result">
            <Reveal>
              <div className="bg-[#0b1220] text-white rounded-3xl p-8 md:p-10 relative overflow-hidden">
                <div className="absolute -right-16 -top-16 w-64 h-64 bg-brand/30 blur-3xl rounded-full" />
                <div className="relative">
                  <span className="inline-flex items-center gap-2 text-xs font-semibold text-blue-200 bg-white/5 border border-blue-400/20 rounded-full px-3 py-1.5 mb-4"><Target size={13} /> Recommended Growth Plan</span>
                  <h2 className="font-heading text-2xl md:text-3xl font-extrabold leading-tight">{plan.headline}</h2>
                  {plan.summary && <p className="text-slate-300 mt-3">{plan.summary}</p>}
                  {plan.suggested_bundle && (
                    <div className="mt-5 inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-xl px-4 py-2 text-sm font-semibold">Suggested package: <span className="text-blue-200">{plan.suggested_bundle}</span></div>
                  )}
                </div>
              </div>
            </Reveal>

            <h3 className="font-heading text-lg font-bold text-slate-900 mt-8 mb-3">Recommended services, in priority order</h3>
            <div className="space-y-3">
              {plan.recommended.map((s, i) => (
                <Reveal key={s.slug} delay={i * 0.05}>
                  <Link to={s.url} data-testid={`plan-svc-${s.slug}`} className="group flex items-start gap-4 bg-white border border-slate-200 rounded-2xl p-5 card-shadow hover-lift">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-brand font-bold text-sm">{i + 1}</span>
                    <span className="min-w-0 flex-1">
                      <span className="font-heading font-bold text-slate-900 flex items-center gap-2">{s.label} <ArrowUpRight size={16} className="text-slate-300 group-hover:text-brand transition-colors" /></span>
                      {s.why && <span className="block text-sm text-slate-500 mt-1">{s.why}</span>}
                    </span>
                  </Link>
                </Reveal>
              ))}
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              {plan.first_steps?.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-2xl p-5 card-shadow">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">First steps</div>
                  <ul className="space-y-2">{plan.first_steps.map((s, i) => <li key={i} className="flex gap-2.5 text-sm text-slate-600"><CheckCircle2 size={16} className="text-green-600 mt-0.5 shrink-0" /> {s}</li>)}</ul>
                </div>
              )}
              {plan.expected_outcomes?.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-2xl p-5 card-shadow">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">What to expect</div>
                  <ul className="space-y-2">{plan.expected_outcomes.map((s, i) => <li key={i} className="flex gap-2.5 text-sm text-slate-600"><ArrowRight size={15} className="text-brand mt-0.5 shrink-0" /> {s}</li>)}</ul>
                </div>
              )}
            </div>

            {/* Proposal capture → CRM lead + emailed branded PDF */}
            <div className="mt-8 rounded-2xl border-2 border-brand/25 bg-white p-6 md:p-7 card-shadow" data-testid="advisor-proposal-card">
              {pSent ? (
                <div className="text-center py-4" data-testid="advisor-proposal-sent">
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-green-50 text-green-600"><CheckCircle2 size={24} /></div>
                  <div className="font-heading text-xl font-bold text-slate-900">Your proposal is on its way!</div>
                  <p className="mt-2 text-sm text-slate-500">We've emailed a branded PDF proposal to <span className="font-semibold text-slate-700">{pEmail}</span>. A WebsiteTech growth specialist will follow up shortly.</p>
                </div>
              ) : (
                <div className="grid gap-5 md:grid-cols-2 md:items-center">
                  <div>
                    <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-brand"><FileText size={14} /> Branded PDF proposal</div>
                    <h3 className="font-heading text-xl font-bold text-slate-900 mt-2">Get this plan as a proposal</h3>
                    <p className="text-sm text-slate-500 mt-1.5">We'll email you a branded PDF proposal and have a growth specialist reach out with next steps.</p>
                  </div>
                  <div className="space-y-3">
                    <input value={pName} onChange={(e) => setPName(e.target.value)} placeholder="Your name" data-testid="proposal-name" className={inputCls} />
                    <input value={pEmail} onChange={(e) => setPEmail(e.target.value)} placeholder="Work email" type="email" data-testid="proposal-email" className={inputCls} />
                    <input value={pPhone} onChange={(e) => setPPhone(e.target.value)} placeholder="Phone / WhatsApp (optional)" data-testid="proposal-phone" className={inputCls} />
                    <button onClick={sendProposal} disabled={pSubmitting} data-testid="proposal-submit" className="btn-primary rounded-xl px-6 py-3 flex items-center justify-center gap-2 text-sm w-full disabled:opacity-60">
                      {pSubmitting ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />} {pSubmitting ? "Sending…" : "Email me the proposal"}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <button onClick={() => setConsultOpen(true)} data-testid="advisor-consult" className="btn-outline rounded-xl px-6 py-3.5 flex items-center gap-2 text-sm">Talk to a specialist <ArrowRight size={15} /></button>
              <Link to="/lead-generation" className="rounded-xl px-6 py-3.5 flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-brand">Explore All Services</Link>
            </div>
          </section>
        )}
      </main>
      <Footer />
      <ConsultationDialog open={consultOpen} onOpenChange={setConsultOpen} context={{ service_interest: plan?.suggested_bundle || "Lead Generation", source: "AI Growth Advisor" }} />
    </>
  );
}
